export const normalizeAmount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const formatKes = (value) => {
  return `KES ${normalizeAmount(value).toLocaleString()}`;
};

export const toDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === 'function') {
    const converted = value.toDate();
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value?.seconds === 'number') {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatShortDate = (value, fallback = '—') => {
  const dateValue = toDateValue(value);

  if (!dateValue) {
    return fallback;
  }

  return dateValue.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (value, fallback = '—') => {
  const dateValue = toDateValue(value);

  if (!dateValue) {
    return fallback;
  }

  return dateValue.toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const getMonthKey = (value = new Date()) => {
  const dateValue = toDateValue(value) || new Date();
  const month = `${dateValue.getMonth() + 1}`.padStart(2, '0');
  return `${dateValue.getFullYear()}-${month}`;
};

export const getMonthLabel = (monthKey) => {
  if (!monthKey || typeof monthKey !== 'string' || !monthKey.includes('-')) {
    return 'Current month';
  }

  const [year, month] = monthKey.split('-').map(Number);
  const parsed = new Date(year, (month || 1) - 1, 1);

  if (Number.isNaN(parsed.getTime())) {
    return 'Current month';
  }

  return parsed.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'long'
  });
};

export const getDateInputValue = (value = new Date()) => {
  const dateValue = toDateValue(value) || new Date();
  return new Date(dateValue.getTime() - dateValue.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

export const getDateTimeInputValue = (value = new Date()) => {
  const dateValue = toDateValue(value) || new Date();
  return new Date(dateValue.getTime() - dateValue.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const resolvePaymentDate = (payment) => {
  return toDateValue(payment.paymentDate) || toDateValue(payment.createdAt) || new Date();
};

export const resolveBillPaymentPeriodKey = (payment) => {
  return payment.billingPeriodKey || payment.billPeriodKey || getMonthKey(resolvePaymentDate(payment));
};

export const resolveRequisitionPaymentPeriodKey = (payment) => {
  return payment.paymentPeriodKey || payment.requisitionPeriodKey || getMonthKey(resolvePaymentDate(payment));
};

export const resolveSalaryPaymentPeriodKey = (payment) => {
  return payment.salaryPeriodKey || payment.payrollMonth || getMonthKey(resolvePaymentDate(payment));
};

const getBalanceTone = (expectedAmount, paidAmount) => {
  if (expectedAmount <= 0) {
    return 'muted';
  }

  if (paidAmount <= 0) {
    return 'danger';
  }

  if (paidAmount >= expectedAmount) {
    return 'success';
  }

  return 'warning';
};

export const summarizeFixedBills = ({
  bills,
  vendorsById,
  branchesById,
  payments,
  periodKey
}) => {
  return bills.map((bill) => {
    const vendor = vendorsById[bill.vendor] || {};
    const branchId = vendor.branch || bill.branch || 'General';
    const billPayments = payments
      .filter(
        (payment) =>
          payment.billId === bill.id &&
          resolveBillPaymentPeriodKey(payment) === periodKey
      )
      .sort((first, second) => {
        const firstTime = resolvePaymentDate(first)?.getTime() || 0;
        const secondTime = resolvePaymentDate(second)?.getTime() || 0;
        return secondTime - firstTime;
      });
    const expectedAmount = normalizeAmount(bill.billAmount);
    const paidAmount = billPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
    const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

    return {
      ...bill,
      vendorName: vendor.vendorName || 'Vendor not found',
      vendorServices: vendor.vendorServices || '',
      vendorEmail: vendor.vendorEmail || '',
      vendorPhone: vendor.vendorPhone || '',
      serviceDesc: vendor.serviceDesc || '',
      paymentDetails: vendor.paymentDetails || '',
      branchId,
      branchName: branchId === 'General' ? 'General' : branchesById[branchId] || 'Campus not set',
      expectedAmount,
      paidAmount,
      remainingAmount,
      progress: expectedAmount > 0 ? Math.min((paidAmount / expectedAmount) * 100, 100) : 0,
      paymentTone: getBalanceTone(expectedAmount, paidAmount),
      paymentStatus:
        expectedAmount <= 0
          ? 'Amount not set'
          : remainingAmount <= 0
            ? 'Settled'
            : paidAmount > 0
              ? 'Partially paid'
              : 'Not paid',
      paymentCount: billPayments.length,
      lastPaymentDate: billPayments[0] ? resolvePaymentDate(billPayments[0]) : null
    };
  });
};

export const summarizeRequisitions = ({
  requisitions,
  branchesById,
  payments
}) => {
  return requisitions.map((item) => {
    const requisitionPayments = payments
      .filter((payment) => payment.requisitionId === item.id)
      .sort((first, second) => {
        const firstTime = resolvePaymentDate(first)?.getTime() || 0;
        const secondTime = resolvePaymentDate(second)?.getTime() || 0;
        return secondTime - firstTime;
      });
    const expectedAmount = normalizeAmount(item.totalAmount);
    const paidAmount = requisitionPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
    const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

    return {
      ...item,
      branchName:
        item.selectedCampus === 'General'
          ? 'General'
          : branchesById[item.selectedCampus] || 'Campus not set',
      expectedAmount,
      paidAmount,
      remainingAmount,
      progress: expectedAmount > 0 ? Math.min((paidAmount / expectedAmount) * 100, 100) : 0,
      paymentTone: getBalanceTone(expectedAmount, paidAmount),
      paymentStatus:
        expectedAmount <= 0
          ? 'Amount not set'
          : remainingAmount <= 0
            ? 'Settled'
            : paidAmount > 0
              ? 'Partially paid'
              : 'Not paid',
      paymentCount: requisitionPayments.length,
      lastPaymentDate: requisitionPayments[0] ? resolvePaymentDate(requisitionPayments[0]) : null
    };
  });
};

export const summarizePayroll = ({
  employees,
  payments,
  periodKey
}) => {
  return employees.map((employee) => {
    const salaryPayments = payments
      .filter(
        (payment) =>
          payment.employeeId === employee.id &&
          resolveSalaryPaymentPeriodKey(payment) === periodKey
      )
      .sort((first, second) => {
        const firstTime = resolvePaymentDate(first)?.getTime() || 0;
        const secondTime = resolvePaymentDate(second)?.getTime() || 0;
        return secondTime - firstTime;
      });
    const expectedAmount = normalizeAmount(employee.salary);
    const paidAmount = salaryPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
    const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

    return {
      ...employee,
      expectedAmount,
      paidAmount,
      remainingAmount,
      progress: expectedAmount > 0 ? Math.min((paidAmount / expectedAmount) * 100, 100) : 0,
      paymentTone: getBalanceTone(expectedAmount, paidAmount),
      paymentStatus:
        expectedAmount <= 0
          ? 'Amount not set'
          : remainingAmount <= 0
            ? 'Settled'
            : paidAmount > 0
              ? 'Partially paid'
              : 'Not paid',
      paymentCount: salaryPayments.length,
      lastPaymentDate: salaryPayments[0] ? resolvePaymentDate(salaryPayments[0]) : null
    };
  });
};
